import { useCallback, useRef, useState } from 'react';

import { isEqual } from 'lodash-es';
import { useCustomCompareCallback } from 'use-custom-compare';

import { Modal } from '@change/ds-core-ui';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { ConfirmModalData } from './ConfirmModalContent';
import { ConfirmModalContent } from './ConfirmModalContent';

export type ConfirmModalOptions = ConfirmModalData;

export type Confirm = (optionsOverrides?: Partial<ConfirmModalOptions>) => Promise<boolean>;

/**
 * Imperative confirm modal built on the DS `Modal`.
 *
 * Returns a tuple `[confirm, confirmModal]`:
 * - `confirm(optionsOverrides?)` opens the modal and resolves to `true` when the user confirms,
 *   or `false` when they cancel, close, press ESC, or click outside.
 * - `confirmModal` is the React element that must be rendered in the consumer's tree.
 *
 * Unlike the legacy `createModalHook` (which rendered into a global portal), the consumer is
 * responsible for rendering `confirmModal`.
 *
 * Options can be passed to the hook itself, and/or to the `confirm` function (which take precedence).
 *
 * @example
 * const [confirm, confirmModal] = useConfirm({
 *   title: 'Are you sure?',
 *   text: ['paragraph 1', 'paragraph 2'],
 * });
 * // ...
 * if (await confirm()) {
 *   // do something
 * }
 * // render `{confirmModal}` somewhere in the component tree
 */
export function useConfirm(
	options: Partial<ConfirmModalOptions> = {},
): [confirm: Confirm, confirmModal: React.ReactElement] {
	const { translate } = useI18n();

	const [isOpen, setIsOpen] = useState(false);
	const [activeOptions, setActiveOptions] = useState<Partial<ConfirmModalOptions>>(options);

	const resolverRef = useRef<((value: boolean) => void) | null>(null);
	const resultRef = useRef(false);

	const settle = useCallback(() => {
		setIsOpen(false);
		resolverRef.current?.(resultRef.current);
		resolverRef.current = null;
		resultRef.current = false;
	}, []);

	const confirm = useCustomCompareCallback<Confirm, [Partial<ConfirmModalOptions>]>(
		async (optionsOverrides = {}) => {
			setActiveOptions({ ...options, ...optionsOverrides });
			resultRef.current = false;
			setIsOpen(true);

			/**
			 * Resolve any prior pending promise so it doesn't hang in case the modal
			 * is opened again before the previous result was settled.
			 */
			resolverRef.current?.(false);

			// oxlint-disable-next-line promise/avoid-new
			const promise = new Promise<boolean>((resolve) => {
				resolverRef.current = resolve;
			});

			return promise;
		},
		[options],
		(prevDeps, nextDeps) => isEqual(prevDeps, nextDeps),
	);

	// In controlled mode the DS `Modal` does not auto-close on footer button press, so we
	// record the choice and close the modal explicitly. `settle` then resolves the promise.
	const handleAccept = useCallback(() => {
		resultRef.current = true;
		settle();
	}, [settle]);

	const handleReject = useCallback(() => {
		resultRef.current = false;
		settle();
	}, [settle]);

	const confirmModal = (
		<Modal
			isOpen={isOpen}
			onClose={settle}
			onOpenChange={(open) => {
				if (!open) settle();
			}}
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
		>
			<ConfirmModalContent {...activeOptions} onAccept={handleAccept} onReject={handleReject} />
		</Modal>
	);

	return [confirm, confirmModal];
}
